import enUS from './locales/en-US.json';
import enAU from './locales/en-AU.json';
import enCA from './locales/en-CA.json';
import enGB from './locales/en-GB.json';
import enIE from './locales/en-IE.json';
import enJM from './locales/en-JM.json';
import enNZ from './locales/en-NZ.json';
import bgBG from './locales/bg-BG.json';
import csCZ from './locales/cs-CZ.json';
import daDK from './locales/da-DK.json';
import deAT from './locales/de-AT.json';
import deCH from './locales/de-CH.json';
import deDE from './locales/de-DE.json';
import deLU from './locales/de-LU.json';
import elGR from './locales/el-GR.json';
import esCR from './locales/es-CR.json';
import esES from './locales/es-ES.json';
import esMX from './locales/es-MX.json';
import etEE from './locales/et-EE.json';
import fiFI from './locales/fi-FI.json';
import frBE from './locales/fr-BE.json';
import frCA from './locales/fr-CA.json';
import frCH from './locales/fr-CH.json';
import frFR from './locales/fr-FR.json';
import frLU from './locales/fr-LU.json';
import hrHR from './locales/hr-HR.json';
import huHU from './locales/hu-HU.json';
import itCH from './locales/it-CH.json';
import itIT from './locales/it-IT.json';
import jaJP from './locales/ja-JP.json';
import ltLT from './locales/lt-LT.json';
import lvLV from './locales/lv-LV.json';
import nlBE from './locales/nl-BE.json';
import nlNL from './locales/nl-NL.json';
import nnNO from './locales/nn-NO.json';
import plPL from './locales/pl-PL.json';
import ptBR from './locales/pt-BR.json';
import ptPT from './locales/pt-PT.json';
import roRO from './locales/ro-RO.json';
import skSK from './locales/sk-SK.json';
import slSI from './locales/sl-SI.json';
import svSE from './locales/sv-SE.json';
import srSP from './locales/Lt-sr-SP.json';

export const locales = {
  'en-US': enUS,
  'en-AU': enAU,
  'en-CA': enCA,
  'en-GB': enGB,
  'en-IE': enIE,
  'en-JM': enJM,
  'en-NZ': enNZ,
  'bg-BG': bgBG,
  'cs-CZ': csCZ,
  'da-DK': daDK,
  'de-AT': deAT,
  'de-CH': deCH,
  'de-DE': deDE,
  'de-LU': deLU,
  'el-GR': elGR,
  'es-CR': esCR,
  'es-ES': esES,
  'es-MX': esMX,
  'et-EE': etEE,
  'fi-FI': fiFI,
  'fr-BE': frBE,
  'fr-CA': frCA,
  'fr-CH': frCH,
  'fr-FR': frFR,
  'fr-LU': frLU,
  'hr-HR': hrHR,
  'hu-HU': huHU,
  'it-CH': itCH,
  'it-IT': itIT,
  'ja-JP': jaJP,
  'lt-LT': ltLT,
  'lv-LV': lvLV,
  'nl-BE': nlBE,
  'nl-NL': nlNL,
  'nn-NO': nnNO,
  'pl-PL': plPL,
  'pt-BR': ptBR,
  'pt-PT': ptPT,
  'ro-RO': roRO,
  'sk-SK': skSK,
  'sl-SI': slSI,
  'sv-SE': svSE,
  'sr-SP': srSP,
};

export const SUPPORTED_LOCALES = Object.keys(locales);
export const DEFAULT_LOCALE = 'en-US';

/**
 * Detect which locale to use, in priority order:
 *  1. `?lng=` URL query param (e.g. ?lng=de-DE)
 *  2. Roland User Profile Settings (profileLang)
 *  3. Browser / OS language (navigator.language)
 *  4. Partial match on language prefix (e.g. "de" → "de-DE")
 *  5. Default: en-US
 */
export function detectLocale(profileLang) {
  const params = new URLSearchParams(window.location.search);
  const urlLang = params.get('lng');
  if (urlLang && SUPPORTED_LOCALES.includes(urlLang)) return urlLang;

  if (profileLang) {
    if (SUPPORTED_LOCALES.includes(profileLang)) return profileLang;
    const profilePrefix = profileLang.split('-')[0];
    const profileMatch = SUPPORTED_LOCALES.find((l) => l.startsWith(`${profilePrefix}-`));
    if (profileMatch) return profileMatch;
  }

  const browserLang = navigator.language;
  if (SUPPORTED_LOCALES.includes(browserLang)) return browserLang;

  const langPrefix = browserLang?.split('-')[0];
  const prefixMatch = SUPPORTED_LOCALES.find((l) => l.startsWith(`${langPrefix}-`));
  if (prefixMatch) return prefixMatch;

  return DEFAULT_LOCALE;
}

/**
 * Resolve a dot-separated key path against a translations object.
 * e.g. getNestedValue(obj, 'COMPONENT.LOG_IN.MENU.HEADING')
 */
function getNestedValue(obj, path) {
  return path.split('.').reduce((acc, key) => acc?.[key], obj);
}

/**
 * Returns a t() function that resolves translation keys for the given locale,
 * with automatic fallback to en-US.
 */
export function createTranslator(locale) {
  const translations = locales[locale] || locales[DEFAULT_LOCALE];
  const fallbackTranslations = locales[DEFAULT_LOCALE];

  return (key, defaultValue) => {
    const value = getNestedValue(translations, key);
    if (value !== undefined && value !== null) return value;

    // Fallback to en-US
    if (locale !== DEFAULT_LOCALE) {
      const fallback = getNestedValue(fallbackTranslations, key);
      if (fallback !== undefined && fallback !== null) return fallback;
    }

    // Last resort: return provided default or the key itself
    return defaultValue ?? key;
  };
}
